import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton
} from 'material-react-table'

import moment from 'moment-timezone'
import _ from 'lodash'

import { MenuItem, Box, IconButton, Typography, Switch, Stack, Tooltip, TextField, InputAdornment } from '@mui/material'

import QrCode2Icon from '@mui/icons-material/QrCode2'
import AppleIcon from '@mui/icons-material/Apple'
import AndroidIcon from '@mui/icons-material/Android'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined'
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'

import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import EditIcon from '@mui/icons-material/Edit'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined'
import AddToHomeScreenOutlinedIcon from '@mui/icons-material/AddToHomeScreenOutlined'
import ListOutlinedIcon from '@mui/icons-material/ListOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'

import DataExportDialog from '/components/data-export-dialog'
import excelExport from './template-list-to-excel'
import StyledPaper from '../paper.jsx'
import { createTableStyles } from '../table-styles'
import { getCurrentSyncCounts } from './sync-state-utils'

const ActiveTemplates = ({ activeTemplates, archivedTemplates, loadingTemplates, viewDetails, viewPasses, duplicateTemplate, editTemplate, syncPasses, archiveTemplate, unarchiveTemplate, issuePasses, notifyAllPasses }) => {
  const theme = useTheme()
  const tableStyles = useMemo(() => createTableStyles(theme), [theme])
  const [dataSource, setDataSource] = useState("Active")
  const [showPreview, setShowPreview] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)

  const totalLivePassesIssued = useMemo(() => _.sum(activeTemplates.map(t => t.live_passes_issued)))
  const totalTestPassesIssued = useMemo(() => _.sum(activeTemplates.map(t => t.test_passes_issued)))
  const totalScannerPasses = useMemo(() => _.sum(activeTemplates.map(t => t.managed_pass_template)))
  const totalLiveActive = useMemo(() => _.sum(activeTemplates.map(t => t.num_live_active_passes)))
  const totalLiveExpired = useMemo(() => _.sum(activeTemplates.map(t => t.num_live_expired_passes)))
  const totalInstalledApple = useMemo(() => _.sum(activeTemplates.map(t => t.live_passes_installed_apple)))
  const totalInstalledGoogle = useMemo(() => _.sum(activeTemplates.map(t => t.live_passes_installed_google)))
  const totalUnsynced = useMemo(() => _.sum(activeTemplates.map(t => getCurrentSyncCounts(t).unique)), [activeTemplates])

  const navigate = useNavigate()

  const data = useMemo(() => {
    const sourceTemplates = dataSource === "Active" ? activeTemplates : archivedTemplates
    const search = searchTerm.trim().toLowerCase()

    if (!search) return sourceTemplates

    return sourceTemplates.filter(template =>
      String(template.template_id).toLowerCase().includes(search) ||
      String(template.name ?? "").toLowerCase().includes(search)
    )
  }, [dataSource, activeTemplates, archivedTemplates, searchTerm])

  const columns = useMemo(() => [
    {
      accessorKey: 'template_id',
      header: 'Template ID',
      filterVariant: 'autocomplete',
      size: 200,
      enableClickToCopy: true,
      Cell: ({ cell }) => <><ContentCopyOutlinedIcon className='mr-5 grey' /> {cell.getValue()}</>,
    },
    {
      accessorKey: 'name',
      header: 'Template Name',
      filterVariant: 'autocomplete',
      size: 400,
      Cell: ({ cell, row }) => <div className='link' onClick={() => { 
        viewDetails(row.original.template_id) }}><OpenInNewIcon className='mr-5 grey' />{cell.getValue()
      }</div>
    },
    {
      accessorKey: 'group_id',
      header: 'Template Group',
      filterVariant: 'autocomplete',
      size: 100,
      Footer: () => "Totals"
    },
    {
      accessorKey: 'managed_pass_template',
      header: 'Smartix Scanner',
      filterVariant: 'autocomplete',
      size: 20,
      Cell: ({ cell }) => cell.getValue() === 1 ? <QrCode2Icon /> : null,
      muiTableBodyCellProps: {
        align: 'center',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalScannerPasses
    },
    {
      accessorKey: 'live_passes_issued',
      header: 'Issued',
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => numberFormat(cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalLivePassesIssued
    },
    {
      accessorKey: 'num_live_active_passes',
      header: 'Current',
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => numberFormat(cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalLiveActive
    },
    {
      accessorKey: 'num_live_expired_passes',
      header: 'Expired',
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => numberFormat(cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalLiveExpired
    },
    {
      accessorKey: 'live_passes_installed_apple',
      header: <><AppleIcon /> Installed</>,
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => numberFormat(cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalInstalledApple
    },
    {
      accessorKey: 'live_passes_installed_google',
      header: <><AndroidIcon /> Installed</>,
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => numberFormat(cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalInstalledGoogle
    },
    {
      id: "sync",
      accessorFn: (originalRow) => getCurrentSyncCounts(originalRow).unique,
      header: <><SyncOutlinedIcon /> Affected</>,
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => numberFormat(cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalUnsynced
    },
    {
      accessorKey: 'test_passes_issued',
      header: 'Test Passes',
      filterVariant: 'range',
      filterFn: 'between',
      size: 20,
      Cell: ({ cell }) => numberFormat(cell.getValue()),
      muiTableBodyCellProps: {
        align: 'right',
      },
      muiTableFooterCellProps: {
        align: 'right',
      },
      Footer: () => totalTestPassesIssued
    },
    {
      id: "creation_timestamp",
      accessorFn: (originalRow) => moment(originalRow.creation_timestamp, "YYYYMMDDHHmmss"),
      header: 'Created',
      Cell: ({ cell }) => cell.getValue().format("DD MMM YYYY HH:mm"),
      filterVariant: 'date-range',
      sortingFn: 'datetime',
      size: 200
    },
    {
      id: "last_modified_timestamp",
      accessorFn: (originalRow) => moment(originalRow.last_modified_timestamp, "YYYYMMDDHHmmss"),
      header: 'Last Modified',
      Cell: ({ cell }) => cell.getValue().format("DD MMM YYYY HH:mm"),
      filterVariant: 'date-range',
      sortingFn: 'datetime',
      size: 200
    },
  ])

  const table = useMaterialReactTable({
    columns,
    data,
    state: {
      isLoading: loadingTemplates
    },
    initialState: { showColumnFilters: false },
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableRowActions: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    positionToolbarAlertBanner: "none",
    getRowId: (originalRow) => originalRow.id,
    layoutMode: 'semantic',
    ...tableStyles,
    renderRowActionMenuItems: ({ closeMenu, row }) => dataSource === "Active" ?
      [
        <MenuItem key={1} onClick={() => { viewDetails(row.original.template_id); closeMenu() }}><OpenInNewIcon className='mr-2' /> Template Details</MenuItem>,
        <MenuItem key={2} onClick={() => { editTemplate(row.original.template_id); closeMenu() }}><EditIcon className='mr-2' /> Edit Template</MenuItem>,
        <MenuItem key={3} onClick={() => { duplicateTemplate(row.original.template_id); closeMenu() }}><ContentCopyOutlinedIcon className='mr-2' /> Duplicate Template</MenuItem>,
        <MenuItem key={4} onClick={() => { archiveTemplate(row.original.template_id); closeMenu() }}><ArchiveOutlinedIcon className='mr-2' /> Archive Template</MenuItem>,
        <MenuItem key={5} onClick={() => { syncPasses(row.original.template_id); closeMenu() }}><SyncOutlinedIcon className='mr-2' /> Synchronise Passes</MenuItem>,
        <MenuItem key={6} onClick={() => { viewPasses(row.original.template_id); closeMenu() }}><ListOutlinedIcon className='mr-2' /> View Issued Passes </MenuItem>,
        <MenuItem key={7} onClick={() => { issuePasses(row.original.template_id); closeMenu() }}><AddToHomeScreenOutlinedIcon className='mr-2' /> Create Passes</MenuItem>,
        <MenuItem key={8} onClick={() => { notifyAllPasses(row.original.template_id); closeMenu() }}><EmailOutlinedIcon className='mr-2' /> Notify All Passes</MenuItem>,
      ]
      :
      [
        <MenuItem key={1} onClick={() => { viewDetails(row.original.template_id); closeMenu() }}><OpenInNewIcon className='mr-2' /> Template Details</MenuItem>,
        <MenuItem key={2} onClick={() => { editTemplate(row.original.template_id); closeMenu() }}><EditIcon className='mr-2' /> Edit Template</MenuItem>,
        <MenuItem key={3} onClick={() => { duplicateTemplate(row.original.template_id); closeMenu() }}><ContentCopyOutlinedIcon className='mr-2' /> Duplicate Template</MenuItem>,
        <MenuItem key={4} onClick={() => { unarchiveTemplate(row.original.template_id); closeMenu() }}><UnarchiveOutlinedIcon className='mr-2' /> Un-Archive Template</MenuItem>,
        <MenuItem key={6} onClick={() => { viewPasses(row.original.template_id); closeMenu() }}><ListOutlinedIcon className='mr-2' /> View Issued Passes </MenuItem>,
      ],
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title="Export"><IconButton onClick={() => setDataExportDialogOpen(true)}><SaveAltIcon /></IconButton></Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <ToolbarControls
        activeTemplates={activeTemplates}
        archivedTemplates={archivedTemplates}
        dataSource={dataSource}
        navigate={navigate}
        searchTerm={searchTerm}
        setDataSource={setDataSource}
        setSearchTerm={setSearchTerm}
        setShowPreview={setShowPreview}
        showPreview={showPreview}
      />
    )
  })

  return (
    <Box sx={{ height: 'calc(100vh - 185px)', width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <StyledPaper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} height="100%">
        {showPreview
          ?
          <>
            <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
              <ToolbarControls
                activeTemplates={activeTemplates}
                archivedTemplates={archivedTemplates}
                dataSource={dataSource}
                navigate={navigate}
                searchTerm={searchTerm}
                setDataSource={setDataSource}
                setSearchTerm={setSearchTerm}
                setShowPreview={setShowPreview}
                showPreview={showPreview}
              />
            </Box>
            <TemplatePreviewGrid templates={data} viewDetails={viewDetails} />
          </>
          :
          <MaterialReactTable table={table} />}
        <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName="templates" data={{ active: activeTemplates, archived: archivedTemplates }} excelExport={excelExport} />
      </StyledPaper>
    </Box>)
}

export default ActiveTemplates

const numberFormat = value => <span className={value === 0 ? "grey" : ""}>{value?.toLocaleString()}</span>

const ToolbarControls = ({ activeTemplates, archivedTemplates, dataSource, navigate, searchTerm, setDataSource, setSearchTerm, setShowPreview, showPreview }) => {
  return (
    <Box sx={{ width: '100%', px: 2, py: 1.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', minWidth: 260 }}>
          <IconButton onClick={() => navigate("/create-template")} className='link'><AddCircleOutlineOutlinedIcon /></IconButton>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography>Archived Templates ({archivedTemplates.length})</Typography>
            <Switch checked={dataSource === "Active"} onChange={e => setDataSource(e.target.checked ? "Active" : "Archived")} />
            <Typography>Active Templates ({activeTemplates.length})</Typography>
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', flex: '1 1 120px' }}>
          <Tooltip title={showPreview ? "Show table view" : "Show preview view"}>
            <IconButton onClick={() => setShowPreview(!showPreview)}>
              {showPreview ? <TableRowsOutlinedIcon /> : <GridViewOutlinedIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' }, flex: '1 1 320px', minWidth: 260 }}>
          <TextField
            size="small"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search by template id or name"
            fullWidth
            sx={{ maxWidth: 420 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon />
                  </InputAdornment>
                )
              }
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}

const TemplatePreviewGrid = ({ templates, viewDetails }) => {
  return (
    <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'stretch' }}>
        {templates.map(template => (
          <StyledPaper
            key={template.template_id}
            sx={{
              width: 320,
              maxWidth: '100%',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              alignItems: 'center'
            }}
          >
            <PreviewTemplateId templateId={template.template_id} />

            <Box
              className='link'
              onClick={() => viewDetails(template.template_id)}
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                cursor: 'pointer',
                my: 1
              }}
            >
              <Box
                component="img"
                src={templatePreviewImageUrl(template)}
                alt={`${template.name} preview`}
                sx={{
                  width: '100%',
                  maxWidth: 280,
                  borderRadius: 1,
                  boxShadow: 2,
                  backgroundColor: '#f7f7f7'
                }}
              />
            </Box>

            <Typography
              className='link'
              onClick={() => viewDetails(template.template_id)}
              sx={{
                textAlign: 'center',
                cursor: 'pointer',
                wordBreak: 'break-word'
              }}
            >
              {template.name}
            </Typography>
          </StyledPaper>
        ))}
      </Box>
    </Box>
  )
}

const PreviewTemplateId = ({ templateId }) => {
  const [title, setTitle] = useState("Click to copy")

  return (
    <CopyToClipboard text={templateId} onCopy={() => setTitle("copied")}>
      <Tooltip title={title}>
        <Typography
          sx={{
            width: '100%',
            textAlign: 'right',
            wordBreak: 'break-word',
            color: 'text.secondary',
            cursor: 'pointer',
            fontSize : 12
          }}
        >
          {templateId} <ContentCopyOutlinedIcon sx={{ fontSize: 20, verticalAlign: 'text-bottom', color: 'text.secondary' }} />
        </Typography>
      </Tooltip>
    </CopyToClipboard>
  )
}

const templatePreviewImageUrl = template => `https://s3.eu-west-1.amazonaws.com/images.smartix.uk/${template.client_id}/template_previews/${template.template_id}.png?v=${template.last_modified_timestamp}`
